import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseController } from '@rensa/shared/base-controller';
import { ActivatedRoute, Router } from '@angular/router';
import { ItemService } from '@rensa/modules/customer/services/item.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ListItem } from '@rensa/modules/customer/models/list-item.model';
import { Event } from '@rensa/modules/customer/models/event.model';
import { ContactStyle } from '../footer/footer.component';
import { MessageType } from '@rensa/shared/components/feedback/feedback.component';
import { HttpErrorResponse } from '@angular/common/http';
import { SelectOption } from '@rensa/shared/components/select/select.component';
import { ItemType } from '@rensa/shared/enums/item-type.enum';
import { environment } from '@rensa/environments/environment';

export interface Person {
  companyName: string;
  name: string;
  debitNo: string;
  city: string;
  email: string;
  phoneNumber: string;
  multipleChoice?: string;
  referrer?: string;
}

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.css']
})
export class RegistrationComponent extends BaseController implements OnInit, OnDestroy {

  eventId: string;
  itemId: string;
  form: FormGroup;
  formColleague: FormGroup;
  ContactStyle = ContactStyle;
  itemTypeEnum = ItemType;
  itemType: ItemType;
  MessageType = MessageType;
  registrationError: string;
  registrationColleagueError: string;

  showRegisterForm = true;
  registerSuccess = false;
  registerColleagueSuccess = false;

  colleagueRegistrationCounter = 0;

  isEventFull = false;

  disableButton = false;

  registeredPerson: Person;

  event: Event;
  loadingEvent = true;
  item: ListItem;

  valueMap = new Map<string, number | string>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private itemService: ItemService,
  ) {
    super();
    this.itemType = environment.itemType;

    this.addSubscription(this.route.params.subscribe(next => {
      this.eventId = next.eventId;
      this.itemId = next.trainingId;
      if (this.itemId) {
        this.fetchItem();
      }
    }));
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      companyName: ['', [Validators.required]],
      name: ['', [Validators.required]],
      debitNo: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      city: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required]],
      multipleChoice: ['']
    });

    this.formColleague = this.formBuilder.group({
      name: ['', [Validators.required]],
      companyName: [],
      debitNo: [],
      city: [],
      email: ['', Validators.email],
      phoneNumber: [],
      referrer: [],
      multipleChoice: ['']
    });
  }

  ngOnDestroy(): void {
    this._ngOnDestroy();
  }

  register(person: Person): void {
    this.registrationError = null;
    this.disableButton = true;
    this.itemService.register(this.itemId, this.eventId, person)
      .toPromise()
      .then(() => this.handlePersonRegistered(person))
      .catch((error) => this.handlePersonRegistrationError(error))
      .finally(() => this.disableButton = false);
  }

  registerColleague(colleague: Person): void {
    this.registrationColleagueError = null;
    colleague = this.fillMissingFields(colleague);
    this.disableButton = true;
    this.itemService.register(this.itemId, this.eventId, colleague)
      .toPromise()
      .then(() => this.handleColleagueRegistered())
      .catch((error: HttpErrorResponse) => this.registrationColleagueError = error.message)
      .finally(() => this.disableButton = false);
  }

  hasGuestlistForced(): boolean {
    return !this.event?.allowNewRegistrations && this.event?.enableGuestlist;
  }

  isFull(): boolean {
    return this.event?.numberOfAttendees === this.event?.maxAttendees || this.hasGuestlistForced();
  }

  isFullOrRegisteredMaxInvitees(): boolean {
    return (this.colleagueRegistrationCounter >= this.event?.maxInvitees) || this.isFull();
  }

  getMultipleChoiceOptions(options: string[]): SelectOption[] {
    const opts = options?.map(option => {
      return {value: option, text: option} as SelectOption;
    });
    opts.unshift({text: 'Selecteer een optie', value: null});
    return opts;
  }

  private fetchItem(): void {
    this.itemService.fetchBy(this.itemId)
      .toPromise()
      .then((item: ListItem) => {
        this.item = item;
        this.event = this.item.events.find(element => element.id === this.eventId);
        this.loadingEvent = false;
        this.setMultipleChoiceRequiredIfAvailable();
        this.fillValueMapForFooter();
      })
      .catch(error => {
        console.error(error);
        this.loadingEvent = false;
        this.router.navigate(['../'], { relativeTo: this.route });
      });
  }

  private setMultipleChoiceRequiredIfAvailable(): void {
    if (this.item.multipleChoice?.title) {
      this.form.get('multipleChoice').setValidators(Validators.required);
      this.formColleague.get('multipleChoice').setValidators(Validators.required);
    }
  }

  private fillValueMapForFooter(): void {
    this.valueMap.set('maxInvitees', this.event?.maxInvitees);
    this.valueMap.set('location', this.event?.location?.city);
    this.valueMap.set('date', this.event?.startDateTime);
    this.valueMap.set('eventId', this.event?.id);
    this.valueMap.set('title', this.item?.title);
  }

  private fillMissingFields(colleague: Person): Person {
    colleague.debitNo = this.registeredPerson.debitNo;
    colleague.companyName = this.registeredPerson.companyName;
    colleague.city = this.registeredPerson.city;
    colleague.referrer = this.registeredPerson.name;
    if (!colleague.email) {
      colleague.email = this.registeredPerson.email;
    }
    return colleague;
  }

  private handleColleagueRegistered(): void {
    this.colleagueRegistrationCounter += 1;
    this.registerColleagueSuccess = true;
    this.registerSuccess = false;
    this.formColleague.reset();
    this.fetchItem();
  }

  private handlePersonRegistered(person: Person): void {
    this.registeredPerson = person;
    this.showRegisterForm = false;
    this.registerSuccess = true;
    this.fetchItem();
  }

  private handlePersonRegistrationError(error: HttpErrorResponse): void {
    if (error.error === "EVENT_CLOSED") {
      this.registrationError = "Let op! De inschrijving voor dit evenement is gesloten. Je bent niet aangemeld.";
    }
    else {
      this.registrationError = error.message;
    }
    
    this.showRegisterForm = true;
    this.registerSuccess = false;
  }
}
